<template>
  <form id="case-tour-update-form" class="d-flex flex-column justify-content-between h-100">
    <fieldset>
      <legend class="visually-hidden">{{ $t("clientTour.tourBasicInformation") }}</legend>

      <v-form-input
        id="tour-update-name"
        v-model="name"
        :label="$t('clientTour.tourName')"
        :yup-errors-variable="errors.name"
        autocomplete="off"
        class="col-12 mb-8"
        form-type="outline"
      />

      <v-form
        id="case-create-travel-type-select"
        :label="`${$t('clientTour.travelType')}*`"
        :yup-errors-variable="errors.travelType"
        form-type="outline"
        class="mb-8"
      >
        <v-select
          id="case-create-travel-type-select"
          v-model="travelType"
          :clearable="false"
          :options="travelTypes"
          :reduce="(returnValue) => returnValue.key"
        >
          <template v-slot:no-options>
            <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
          </template>
        </v-select>
      </v-form>

      <v-input-address
        id="case-tour-origin-search"
        ref="case-tour-origin-search"
        :get-address="true"
        :get-radius-bounds="true"
        :label="`${$t('general.shared.origin')}*`"
        :radius-bounds="20000"
        :value="searchLocation(origin)"
        :yup-errors-variable="errors.originSelected"
        class="col-12 mb-8"
        @address="createLocation($event, true)"
        @input="checkLocationInput($event, true)"
      />

      <v-input-address
        id="case-tour-destination-search"
        ref="case-tour-destination-search-ref"
        :get-address="true"
        :get-radius-bounds="true"
        :label="`${$t('general.shared.destination')}*`"
        :radius-bounds="20000"
        :value="searchLocation(destination)"
        :yup-errors-variable="errors.destinationSelected"
        class="col-12 mb-0"
        @address="createLocation($event, false)"
        @input="checkLocationInput($event, false)"
      />
    </fieldset>

    <v-button
      :disabled="!meta.valid || disableButton || !meta.dirty"
      :is-loading="accepted"
      class="btn-icon w-fit align-self-end mt-12"
      size="xs"
      variant="primary"
      @click="updateTour"
    >
      {{ $t("general.button.save") }}
      <v-icon icon="arrow-right" size="xs" space="ms-8" />
    </v-button>
  </form>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { VForm, VFormInput } from "@uniqoders/form";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { useField } from "vee-validate";
import vSelect from "vue-select";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import useFormValidation from "@/helpers/form";
import api from "@/api";
import Tour from "@/api/objects/Tour";
import { mapConcepts } from "@/helpers/ConceptHelper";
import useConcepts from "@/helpers/Concepts";
import VInputAddress from "@/components/vendor/basic/form/VInputAddress.vue";
import Location from "@/api/objects/Location";
import { useToast } from "vue-toastification";

const emit = defineEmits<{
  (e: "reloadCase"): void;
}>();

// Define props with types
const props = defineProps<{
  agency: string;
  case: Tour;
  clientCaseId: number;
}>();

const { t } = useI18n();
const toast = useToast();
const locations = ref<Location[]>([]);
const disableButton = ref<boolean>(false);
const { concepts } = useConcepts();
const travelTypes = computed(() => {
  if (concepts.value && concepts.value["tour.travel_type"]) {
    return mapConcepts(concepts.value["tour.travel_type"]);
  }
  return [];
});

const rules = yup.object({
  name: yup.string().nullable().label(t("clientTour.tourName")),

  travelType: yup.string().required().label(t("clientTour.travelType")),

  originSelected: yup.bool().oneOf([true], t("general.shared.selectOriginMessage")),

  origin: yup.mixed().required().nullable().label(t("general.shared.origin")),

  destination: yup.mixed().required().nullable().label(t("general.shared.destination")),

  destinationSelected: yup.bool().oneOf([true], t("general.shared.selectDestinationMessage")),
});

// Initial values
const initialValues: Record<string, any> = {
  name: props.case.tour_name || props.case.name,
  travelType: props.case.travel_type,
  origin: props.case.origin_id,
  destination: props.case.destination_id,
  originSelected: !!props.case.origin_id,
  destinationSelected: !!props.case.destination_id,
};

const { meta, accepted, errors } = useFormValidation(rules, initialValues);

const { value: name } = useField("name");
const { value: travelType } = useField("travelType");
const { value: origin } = useField("origin");
const { value: destination } = useField("destination");
const { value: originSelected } = useField("originSelected");
const { value: destinationSelected } = useField("destinationSelected");

onMounted(async () => {
  await loadLocations();
});

const checkLocationInput = (value: string, isOrigin = false) => {
  if (isOrigin) {
    originSelected.value = false;
  } else {
    destinationSelected.value = false;
  }
};

const loadLocations = async () => {
  locations.value = await api.location.all();
};

const searchLocation = (locationId: number): string => {
  const location = locations.value.find((loc) => loc.id === locationId);
  return location ? location.name : "";
};

const createLocation = async (address: Record<string, any>, isOrigin = true) => {
  const location: Location = await api.location.create({
    name: address.name,
    provider_id: address.placeId,
    lat: address.lat,
    lng: address.lng,
    country: address.country.longName,
    province: address.regionProvince.longName,
    state: address.stateCommunity.longName,
  });

  await loadLocations();

  if (isOrigin) {
    origin.value = location.id;
    originSelected.value = true;
  } else {
    destination.value = location.id;
    destinationSelected.value = true;
  }
};

const updateTour = async () => {
  let saved = false;
  try {
    accepted.value = true;
    disableButton.value = true;

    const data = {
      case_manager_id: props.case.case_manager_id,
      start_date: props.case.start_date ,
      alt_start_date: props.case.alt_start_date,
      tour_name: name.value,
      travel_type: travelType.value,
      origin_id: origin.value,
      destination_id: destination.value,
    };

    await api.case.updateBasicInfoCase(props.agency, props.clientCaseId, data);

    toast.success(t("general.shared.savedChanges"));
    saved = true;
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || t("general.shared.errorOccurred"));
  } finally {
    disableButton.value = false;
    accepted.value = false;
  }

  if (saved) {
    emit("reloadCase");
  }
};
</script>
